import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  ViewContainerRef
}                                             from '@angular/core';
import { isPlatformBrowser }                  from '@angular/common';
import { ActivatedRoute }                     from '@angular/router';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal }    from '@angular/cdk/portal';
import { ESCAPE }                             from '@angular/cdk/keycodes';

import { fromEvent, Observable, Subject }                 from 'rxjs';
import { distinctUntilChanged, map, pairwise, takeUntil } from 'rxjs/operators';

import { animationStates, drawer, fade } from '@shared/animations/animations';
import { userDropdownMenu }              from '@shared/menus/user-menu';
import { AuthUser }                      from '@models/user/auth-user';
import { UserType }                      from '@models/user/user-type';
import { Menu }                          from '@models/navigation/menu';
import { Anchor }                        from '@models/navigation/anchor';
import { SiteSection }                   from '@models/layout/site-section';
import { Breakpoints }                   from '@utils/breakpoints/breakpoints.enum';

import { BreakpointsService } from '@utils/breakpoints/breakpoints.service';
import { UserService }        from '@services/user.service';
import { OverlayService }     from '@services/overlay.service';


import { AuthOverlayComponent }     from '../auth-overlay/auth-overlay.component';
import { ContractOverlayComponent } from '../contract-overlay/contract-overlay.component';


@Component({
  selector:    'red-toolbar-institutional',
  templateUrl: './toolbar-institutional.component.html',
  styleUrls:   [ './toolbar-institutional.component.scss' ],
  animations:  [ drawer, fade ]
})
export class ToolbarInstitutionalComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly unsubscribe$: Subject<void>;

  public hideToolbar$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  public userType$: Observable<UserType>;

  public desktopLogoUrl:     string;
  public mobileLogoUrl:      string;
  public isMobile:           boolean;
  public isToolbarSolid:     boolean;
  public isMenuOpen:         boolean;
  public isAuthOpen:         boolean;
  public isContractOpen:     boolean;
  public menuOverlay:        OverlayRef;
  public authOverlay:        OverlayRef;
  public contractOverlay:    OverlayRef;
  public menuPortal:         TemplatePortal;
  public authPortal:         ComponentPortal<AuthOverlayComponent>;
  public contractPortal:     ComponentPortal<ContractOverlayComponent>;
  public menuAnimationState: animationStates;
  public overlaysConfig:     OverlayConfig;
  public userMenu:           Anchor[];

  @Input() public type: SiteSection;
  @Input() public menu: Menu;
  @Input() public user: AuthUser;

  @ViewChild('menuTemplate') public menuTemplate: TemplateRef<any>;

  @HostListener('document:keydown', [ '$event' ])
  public handleKeydown(event: KeyboardEvent) {
    // noinspection JSDeprecatedSymbols
    if (event.keyCode === ESCAPE && this.isMenuOpen) this.closeMenuMobile();
  }

  constructor(
    private activatedRoute:     ActivatedRoute,
    private viewContainerRef:   ViewContainerRef,
    private overlay:            Overlay,
    private overlayService:     OverlayService,
    private breakpointsService: BreakpointsService,
    private userService:        UserService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.unsubscribe$ = new Subject<void>();

    this.isMobile       = false;
    this.isMenuOpen     = false;
    this.isAuthOpen     = false;
    this.isContractOpen = false;
    this.desktopLogoUrl = 'assets/images/red1000-logo-toolbar.png';
    this.mobileLogoUrl  = 'assets/images/red1000-logo-toolbar@phone.png';
    this.userMenu       = [];

    this.userType$ = this.userService.getUserType();

    this.isMobile$ = this.breakpointsService.observer([ Breakpoints.TabletPortraitDown ]);

    const positionStrategy = this.overlay
                                 .position()
                                 .global()
                                 .centerVertically()
                                 .centerHorizontally();

    this.overlaysConfig = {
      positionStrategy,
      disposeOnNavigation: true,
      hasBackdrop:         true,
      backdropClass:       [ 'o-backdrop' ]
    };
  }

  public ngOnInit(): void {
    if (isPlatformBrowser(this.platformId) && window) {
      this.hideToolbar$ = fromEvent(window, 'scroll').pipe(
        map(this.mapScrollTop),
        pairwise(),
        map(this.hideToolbar),
        distinctUntilChanged()
      );

      setTimeout(() => this.isToolbarSolid = window.scrollY > 60, 0);
    }

    this.initUserMenu();

    this.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
      this.closeMenuMobile(false);
    });
  }

  public ngAfterViewInit(): void {

    this.activatedRoute.queryParamMap.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe(queryParamMap => {
      if (
        queryParamMap.has('entrar') ||
        (queryParamMap.has('usuarioId') && queryParamMap.has('numToken')) ||
        queryParamMap.has('trocarSenha')
      ) {
        setTimeout(() => this.openAuthOverlay(), 1000);
      } else if (queryParamMap.has('contrato')) {
        setTimeout(() => this.openContractOverlay(), 1000);
      }
    });
  }

  // noinspection DuplicatedCode
  private initUserMenu() {
    this.userMenu = userDropdownMenu;

    this.userMenu.map((item: Anchor) => {
      if (item.slug === 'logout') item.click = (event: Event) => {
        if (event) event.preventDefault();
        this.userService.logout();
      };

      return item;
    });
  }

  public openMobileMenu() {
    this.menuPortal = new TemplatePortal(this.menuTemplate, this.viewContainerRef);

    this.overlayService.open(
      'menu-mobile',
      this.menuPortal,
      { ...this.overlaysConfig, scrollStrategy: this.overlay.scrollStrategies.close() }
    );

    this.menuOverlay = this.overlayService.getOverlayRef('menu-mobile');

    this.menuOverlay.backdropClick().subscribe(() => this.closeMenuMobile());
    this.menuOverlay.detachments().subscribe(() => this.isMenuOpen = false);

    this.menuAnimationState = 'enter';
    this.isMenuOpen         = true;
  }

  private openOverlay(id: string, portal: ComponentPortal<any>) {
    this.overlayService.open(id, portal, {
      ...this.overlaysConfig, scrollStrategy: this.overlay.scrollStrategies.block()
    });
  }

  public openAuthOverlay() {
    if(this.isAuthOpen) return;
    this.authPortal = new ComponentPortal(AuthOverlayComponent, this.viewContainerRef);
  
    this.openOverlay('auth', this.authPortal);
  
    this.authOverlay = this.overlayService.getOverlayRef('auth');
    this.isAuthOpen  = true;
  }

  public openContractOverlay() {
    this.contractPortal = new ComponentPortal(ContractOverlayComponent, this.viewContainerRef);

    this.openOverlay('contract', this.contractPortal);

    this.contractOverlay = this.overlayService.getOverlayRef('contract');
    this.isContractOpen  = true;
  }

  public closeMenuMobile(animate: boolean = true) {

    if (animate) {
      this.menuAnimationState = 'leave';
    } else {
      this.overlayService.close('menu-mobile');
      this.isMenuOpen         = false;
      this.menuAnimationState = 'void';
    }
  }

  public menuAnimationDone() {
    if (this.menuAnimationState === 'leave') {
      this.menuAnimationState = 'void';
      this.closeMenuMobile(false);
    }
  }

  private mapScrollTop = (event): number => event.target.scrollingElement.scrollTop;

  private hideToolbar = ([ before, next ]): boolean => {
    this.isToolbarSolid = next > 60;
    return this.isUserScrollingDown(before, next) && this.isScrollOnRoof(next);
  }

  private isUserScrollingDown = (before: number, next: number): boolean => next > before;

  private isScrollOnRoof = (scrollTop: number): boolean => scrollTop > 180;

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
